import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, FormField, LoadingSkeleton } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, Clock, ShieldAlert, ShieldCheck, FileText, Upload } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";

// Client-side mirror of the backend validation (services/b2c/kyc_service.py).
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACC_RE = /^[0-9]{9,18}$/;
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPT = ".pdf,.jpg,.jpeg,.png";
const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

// File is acceptable when its type is one of the allowed content-types (or, for some browsers
// that leave .type blank, when the extension matches) AND it is at most 5 MB.
const fileTypeOk = (f) =>
  ALLOWED_TYPES.includes(f.type) || /\.(pdf|jpe?g|png)$/i.test(f.name || "");
const validateFile = (f) => {
  if (!f) return null;
  if (!fileTypeOk(f)) return "Only PDF, JPG or PNG files are allowed.";
  if (f.size > MAX_FILE_BYTES) return "File must be 5 MB or smaller.";
  return null;
};

// Banner styling per KYC status. Classes use design-system tint patterns (safelisted hues),
// no raw hex.
const BANNERS = {
  not_submitted: {
    icon: ShieldAlert,
    cls: "bg-slate-50 border-slate-200 text-slate-700",
    iconCls: "text-slate-500",
    title: "Complete KYC to start shipping",
    body: "Verify your PAN (and optionally GSTIN) so we can enable B2C shipping for your account.",
  },
  pending: {
    icon: Clock,
    cls: "bg-amber-50 border-amber-200 text-amber-800",
    iconCls: "text-amber-600",
    title: "KYC under review",
    body: "We're verifying your documents. You'll be able to ship once an admin approves your KYC.",
  },
  approved: {
    icon: ShieldCheck,
    cls: "bg-green-50 border-green-200 text-green-800",
    iconCls: "text-green-600",
    title: "KYC approved",
    body: "Your business is verified — B2C shipping is enabled on your account.",
  },
  rejected: {
    icon: ShieldAlert,
    cls: "bg-red-50 border-red-200 text-red-800",
    iconCls: "text-red-600",
    title: "KYC rejected",
    body: "Please review the remarks below, correct your details and resubmit.",
  },
};

const StatusBanner = ({ status, remarks, submittedAt }) => {
  const b = BANNERS[status] || BANNERS.not_submitted;
  const Icon = b.icon;
  return (
    <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${b.cls}`} data-testid="b2c-kyc-status-banner">
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${b.iconCls}`} />
      <div className="min-w-0">
        <p className="text-sm font-semibold">{b.title}</p>
        <p className="mt-0.5 text-sm">{b.body}</p>
        {status === "rejected" && remarks && (
          <p className="mt-1.5 text-sm font-medium" data-testid="b2c-kyc-reject-remarks">
            Reason: {remarks}
          </p>
        )}
        {submittedAt && (status === "pending" || status === "approved") && (
          <p className="mt-1 text-xs opacity-80">Submitted {formatToIST(submittedAt)}</p>
        )}
      </div>
    </div>
  );
};

// Read-only display of a submitted document URL.
const DocLink = ({ url, label, testid }) => {
  if (!url) return <span className="text-sm text-slate-400">-</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
      data-testid={testid}
    >
      <FileText className="h-4 w-4" /> {label}
    </a>
  );
};

const B2CKyc = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [kyc, setKyc] = useState({ status: "not_submitted" });
  const [submitting, setSubmitting] = useState(false);

  const [pan, setPan] = useState("");
  const [gstin, setGstin] = useState("");
  const [panFile, setPanFile] = useState(null);
  const [gstinFile, setGstinFile] = useState(null);
  const [bank, setBank] = useState({ holder: "", account: "", ifsc: "", bankName: "" });
  const [chequeFile, setChequeFile] = useState(null);
  const [errors, setErrors] = useState({});

  const panFileRef = useRef(null);
  const gstinFileRef = useRef(null);
  const chequeFileRef = useRef(null);

  const fetchKyc = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/b2c/seller/kyc`);
      setKyc(data || { status: "not_submitted" });
      // Pre-fill the editable form with the last submitted values so a rejected seller
      // can tweak rather than retype everything.
      setPan((data?.pan_number || "").toUpperCase());
      setGstin(data?.gstin || "");
      const ba = data?.bank_account || {};
      setBank({
        holder: ba.account_holder_name || "",
        account: ba.account_number || "",
        ifsc: ba.ifsc || "",
        bankName: ba.bank_name || "",
      });
    } catch (e) {
      toast.error(errDetail(e, "Failed to load KYC status"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKyc();
  }, [fetchKyc]);

  const status = kyc?.status || "not_submitted";
  // not_submitted + rejected => editable form; pending + approved => read-only.
  const editable = status === "not_submitted" || status === "rejected";

  const onPanFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    const err = validateFile(f);
    setErrors((p) => ({ ...p, panFile: err || undefined }));
    setPanFile(err ? null : f);
    if (err && panFileRef.current) panFileRef.current.value = "";
  };

  const onGstinFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    const err = validateFile(f);
    setErrors((p) => ({ ...p, gstinFile: err || undefined }));
    setGstinFile(err ? null : f);
    if (err && gstinFileRef.current) gstinFileRef.current.value = "";
  };

  const onChequeFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    const err = validateFile(f);
    setErrors((p) => ({ ...p, chequeFile: err || undefined }));
    setChequeFile(err ? null : f);
    if (err && chequeFileRef.current) chequeFileRef.current.value = "";
  };

  const validate = () => {
    const next = {};
    if (!PAN_RE.test(pan)) next.pan = "Enter a valid PAN (e.g. ABCDE1234F).";
    if (!panFile) next.panFile = "Upload your PAN document.";
    else {
      const fe = validateFile(panFile);
      if (fe) next.panFile = fe;
    }
    if (gstin) {
      if (!GSTIN_RE.test(gstin)) next.gstin = "Enter a valid 15-character GSTIN, or leave it blank.";
    }
    if (gstinFile) {
      const fe = validateFile(gstinFile);
      if (fe) next.gstinFile = fe;
    }
    // Bank account (required — receives COD payouts).
    if (!bank.holder.trim()) next.bankHolder = "Account holder name is required.";
    if (!ACC_RE.test(bank.account.trim())) next.bankAccount = "Enter a valid account number (9–18 digits).";
    if (!IFSC_RE.test(bank.ifsc.toUpperCase().trim())) next.bankIfsc = "Enter a valid IFSC (e.g. HDFC0001234).";
    if (!bank.bankName.trim()) next.bankName = "Bank name is required.";
    if (chequeFile) {
      const fe = validateFile(chequeFile);
      if (fe) next.chequeFile = fe;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("pan_number", pan);
      if (gstin) form.append("gstin", gstin);
      form.append("bank_account_holder_name", bank.holder.trim());
      form.append("bank_account_number", bank.account.trim());
      form.append("bank_ifsc", bank.ifsc.toUpperCase().trim());
      form.append("bank_name", bank.bankName.trim());
      form.append("pan_file", panFile);
      if (gstinFile) form.append("gstin_file", gstinFile);
      if (chequeFile) form.append("cheque_file", chequeFile);
      await axios.post(`${API}/b2c/seller/kyc`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("KYC submitted — we'll review it shortly.");
      // Reset file inputs; re-fetch flips the page into the read-only pending state.
      setPanFile(null);
      setGstinFile(null);
      setChequeFile(null);
      if (panFileRef.current) panFileRef.current.value = "";
      if (gstinFileRef.current) gstinFileRef.current.value = "";
      if (chequeFileRef.current) chequeFileRef.current.value = "";
      fetchKyc();
    } catch (err) {
      toast.error(errDetail(err, "Could not submit KYC. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full max-w-3xl">
        <PageHeader title="KYC Verification" subtitle="Verify your business to enable B2C shipping" />

        {loading ? (
          <LoadingSkeleton variant="form" rows={4} />
        ) : (
          <>
            <StatusBanner status={status} remarks={kyc?.remarks} submittedAt={kyc?.submitted_at} />

            {editable ? (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Business documents</CardTitle>
                  <CardDescription>
                    PAN is required. GSTIN is optional but recommended for tax invoices. Accepted
                    files: PDF, JPG or PNG, up to 5 MB each.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={onSubmit} className="space-y-5" data-testid="b2c-kyc-form">
                    <FormField label="PAN number" htmlFor="b2c-kyc-pan" required error={errors.pan}>
                      <Input
                        id="b2c-kyc-pan"
                        value={pan}
                        onChange={(e) => setPan(e.target.value.toUpperCase())}
                        placeholder="ABCDE1234F"
                        maxLength={10}
                        autoComplete="off"
                        className="uppercase tracking-wider font-mono"
                        data-testid="b2c-kyc-pan-input"
                      />
                    </FormField>

                    <FormField
                      label="PAN document"
                      htmlFor="b2c-kyc-pan-file"
                      required
                      error={errors.panFile}
                      hint="Upload a clear scan or photo of your PAN card."
                    >
                      <input
                        id="b2c-kyc-pan-file"
                        ref={panFileRef}
                        type="file"
                        accept={ACCEPT}
                        onChange={onPanFileChange}
                        className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-brand/10 file:px-4 file:py-2 file:text-sm file:font-medium file:text-brand hover:file:bg-brand/20"
                        data-testid="b2c-kyc-pan-file"
                      />
                    </FormField>

                    <FormField label="GSTIN (optional)" htmlFor="b2c-kyc-gstin" error={errors.gstin}>
                      <Input
                        id="b2c-kyc-gstin"
                        value={gstin}
                        onChange={(e) => setGstin(e.target.value.toUpperCase())}
                        placeholder="22ABCDE1234F1Z5"
                        maxLength={15}
                        autoComplete="off"
                        className="uppercase tracking-wider font-mono"
                        data-testid="b2c-kyc-gstin-input"
                      />
                    </FormField>

                    <FormField
                      label="GSTIN document (optional)"
                      htmlFor="b2c-kyc-gstin-file"
                      error={errors.gstinFile}
                      hint="Upload your GST registration certificate if you have one."
                    >
                      <input
                        id="b2c-kyc-gstin-file"
                        ref={gstinFileRef}
                        type="file"
                        accept={ACCEPT}
                        onChange={onGstinFileChange}
                        className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-brand/10 file:px-4 file:py-2 file:text-sm file:font-medium file:text-brand hover:file:bg-brand/20"
                        data-testid="b2c-kyc-gstin-file"
                      />
                    </FormField>

                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Bank account (for COD payouts)</p>
                        <p className="text-xs text-slate-500">
                          COD collected on your shipments is transferred to this account.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField label="Account holder name" htmlFor="b2c-kyc-bank-holder" required error={errors.bankHolder}>
                          <Input id="b2c-kyc-bank-holder" value={bank.holder}
                            onChange={(e) => setBank({ ...bank, holder: e.target.value })}
                            placeholder="As per bank records" data-testid="b2c-kyc-bank-holder" />
                        </FormField>
                        <FormField label="Account number" htmlFor="b2c-kyc-bank-account" required error={errors.bankAccount}>
                          <Input id="b2c-kyc-bank-account" inputMode="numeric" value={bank.account}
                            onChange={(e) => setBank({ ...bank, account: e.target.value.replace(/\D/g, "").slice(0, 18) })}
                            placeholder="Account number" className="font-mono" data-testid="b2c-kyc-bank-account" />
                        </FormField>
                        <FormField label="IFSC code" htmlFor="b2c-kyc-bank-ifsc" required error={errors.bankIfsc}>
                          <Input id="b2c-kyc-bank-ifsc" value={bank.ifsc}
                            onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase().slice(0, 11) })}
                            placeholder="HDFC0001234" maxLength={11}
                            className="uppercase font-mono tracking-wider" data-testid="b2c-kyc-bank-ifsc" />
                        </FormField>
                        <FormField label="Bank name" htmlFor="b2c-kyc-bank-name" required error={errors.bankName}>
                          <Input id="b2c-kyc-bank-name" value={bank.bankName}
                            onChange={(e) => setBank({ ...bank, bankName: e.target.value })}
                            placeholder="e.g. HDFC Bank" data-testid="b2c-kyc-bank-name" />
                        </FormField>
                      </div>
                      <FormField label="Cancelled cheque (optional)" htmlFor="b2c-kyc-cheque-file"
                        error={errors.chequeFile}
                        hint="A cancelled cheque or passbook helps us verify the account faster.">
                        <input id="b2c-kyc-cheque-file" ref={chequeFileRef} type="file" accept={ACCEPT}
                          onChange={onChequeFileChange}
                          className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-brand/10 file:px-4 file:py-2 file:text-sm file:font-medium file:text-brand hover:file:bg-brand/20"
                          data-testid="b2c-kyc-cheque-file" />
                      </FormField>
                    </div>

                    <div className="flex justify-end pt-1">
                      <Button type="submit" disabled={submitting} data-testid="b2c-kyc-submit">
                        <Upload className="mr-2 h-4 w-4" />
                        {submitting ? "Submitting…" : status === "rejected" ? "Resubmit KYC" : "Submit KYC"}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Submitted details</CardTitle>
                  <CardDescription>
                    {status === "approved"
                      ? "These documents have been verified."
                      : "These documents are under review — they can't be edited while pending."}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">PAN number</Label>
                      <p className="text-sm font-mono font-medium text-slate-800" data-testid="b2c-kyc-ro-pan">
                        {kyc?.pan_number || "-"}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">PAN document</Label>
                      <DocLink url={kyc?.pan_doc_url} label="View PAN" testid="b2c-kyc-ro-pan-doc" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">GSTIN</Label>
                      <p className="text-sm font-mono font-medium text-slate-800" data-testid="b2c-kyc-ro-gstin">
                        {kyc?.gstin || <span className="font-sans text-slate-400">Not provided</span>}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">GSTIN document</Label>
                      <DocLink url={kyc?.gstin_doc_url} label="View GSTIN" testid="b2c-kyc-ro-gstin-doc" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Account holder</Label>
                      <p className="text-sm font-medium text-slate-800">{kyc?.bank_account?.account_holder_name || "-"}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Account number</Label>
                      <p className="text-sm font-mono font-medium text-slate-800">{kyc?.bank_account?.account_number || "-"}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">IFSC</Label>
                      <p className="text-sm font-mono font-medium text-slate-800">{kyc?.bank_account?.ifsc || "-"}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Bank</Label>
                      <p className="text-sm font-medium text-slate-800">{kyc?.bank_account?.bank_name || "-"}</p>
                    </div>
                    {kyc?.bank_cheque_doc_url && (
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Cancelled cheque</Label>
                        <DocLink url={kyc?.bank_cheque_doc_url} label="View cheque" testid="b2c-kyc-ro-cheque-doc" />
                      </div>
                    )}
                  </dl>
                  {status === "approved" && (
                    <div className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-green-50 px-3 py-2 text-sm font-medium text-green-700">
                      <CheckCircle2 className="h-4 w-4" /> Verified — B2C shipping enabled
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </SellerLayout>
  );
};

export default B2CKyc;
